import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Lock, ShieldAlert } from '../../lib/icons';
import { useAuth } from '../../lib/auth';
import { emettreAvoir, emettreFacture, fetchEmission, lignesFigees, peutEmettre, urlPdfOfficiel } from '../../lib/emission';
import { invaliderIndexEmissions } from '../../lib/facturable';

// Déclencheur EXPLICITE de la souche officielle. Ce composant n'émet jamais au
// montage : il consulte seulement l'état d'émission de l'opération.
export const EmissionFacture = ({ operation, onEmis }) => {
  const { user } = useAuth();
  const [emission, setEmission] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [confirme, setConfirme] = useState(false);
  const [motif, setMotif] = useState('');
  const [avoirOuvert, setAvoirOuvert] = useState(false);
  const autorise = peutEmettre(user);

  const charger = useCallback(() => {
    if (operation?.type && operation?.id) fetchEmission(operation.type, operation.id).then(setEmission);
  }, [operation?.type, operation?.id]);

  useEffect(() => {
    charger();
  }, [charger]);

  const emettre = async () => {
    setOccupe(true);
    const { error, data } = await emettreFacture(
      {
        operation_type: operation.type,
        operation_id: String(operation.id),
        libelle: operation.libelle,
        date: operation.date,
        tiers_id: operation.tiersId ?? null,
        tiers_nom: operation.tiers ?? '—',
        tiers_matricule: operation.tiersMatricule ?? null,
        tiers_adresse: operation.tiersAdresse ?? null,
        lignes: lignesFigees(operation.lignes),
        totaux: lignesFigees(operation.totaux),
        montant_total: Number(operation.total ?? 0),
        mentions: operation.mentions ?? null,
        reference_interne: operation.referenceInterne ?? null,
      },
      user
    );
    setOccupe(false);
    setConfirme(false);
    if (error) return toast.error(error);
    setEmission(data);
    invaliderIndexEmissions();
    toast.success(data.deja_emise ? `Facture déjà émise : ${data.numero}` : `Facture officielle ${data.numero} émise et figée.`);
    onEmis?.(data);
  };

  const annuler = async () => {
    const { error, data } = await emettreAvoir(emission.numero, motif, user);
    if (error) return toast.error(error);
    toast.success(`Avoir ${data.numero} émis — la facture ${emission.numero} reste intacte.`);
    invaliderIndexEmissions();
    setAvoirOuvert(false);
    setMotif('');
    charger();
    onEmis?.(data);
  };

  if (emission?.numero) {
    return (
      <div data-testid="emission-etat" className="flex flex-wrap items-center gap-2 border border-emerald-200 bg-emerald-50 px-3 py-2">
        <Lock size={14} className="text-emerald-700" />
        <span className="font-data text-sm font-bold text-emerald-800" data-testid="emission-numero">
          {emission.numero}
        </span>
        <span className="text-[11px] text-emerald-700">
          figée le {String(emission.emis_le).slice(0, 10)} par {emission.emis_par?.nom}
          {emission.annulee_par ? ` · annulée par l’avoir ${emission.annulee_par}` : ''}
        </span>
        <a
          data-testid="emission-pdf"
          href={urlPdfOfficiel(emission.numero)}
          target="_blank"
          rel="noreferrer"
          className="ml-auto border border-emerald-300 px-2 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
        >
          PDF officiel
        </a>
        {autorise && !emission.annulee_par && (
          <button
            data-testid="emission-avoir"
            type="button"
            onClick={() => setAvoirOuvert(!avoirOuvert)}
            className="border border-emerald-300 px-2 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100"
          >
            Annuler par un avoir
          </button>
        )}
        {avoirOuvert && (
          <div className="mt-2 flex w-full flex-wrap items-end gap-2 border-t border-emerald-200 pt-2">
            <label className="flex-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-emerald-700">Motif obligatoire</span>
              <input
                data-testid="emission-avoir-motif"
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Erreur de quantité, retour marchandise…"
                className="h-9 w-full border border-emerald-300 bg-white px-2 text-sm"
              />
            </label>
            <button
              data-testid="emission-avoir-valider"
              type="button"
              disabled={motif.trim().length < 3}
              onClick={annuler}
              className="h-9 bg-emerald-700 px-3 text-xs font-bold text-white disabled:opacity-50"
            >
              Émettre l’avoir
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!autorise) {
    return (
      <p data-testid="emission-non-autorise" className="border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
        Document interne. L’émission d’une facture officielle est réservée à l’Administrateur et au Comptable.
      </p>
    );
  }

  return confirme ? (
    <div data-testid="emission-confirmation" className="flex flex-wrap items-center gap-2 border border-amber-300 bg-amber-50 px-3 py-2">
      <ShieldAlert size={14} className="text-amber-700" />
      <span className="text-[11px] text-amber-800">
        Un numéro officiel continu sera attribué et le contenu sera <strong>figé définitivement</strong>. Aucun mouvement de
        stock ni de caisse n’est créé.
      </span>
      <button
        data-testid="emission-confirmer"
        type="button"
        disabled={occupe}
        onClick={emettre}
        className="ml-auto h-9 bg-slate-900 px-3 text-xs font-bold uppercase tracking-[0.08em] text-white disabled:opacity-50"
      >
        {occupe ? 'Émission…' : 'Confirmer l’émission'}
      </button>
      <button type="button" onClick={() => setConfirme(false)} className="h-9 border border-amber-300 px-3 text-xs font-semibold text-amber-800">
        Annuler
      </button>
    </div>
  ) : (
    <button
      data-testid="emettre-facture-officielle"
      type="button"
      onClick={() => setConfirme(true)}
      className="flex h-10 w-full items-center justify-center gap-2 border-2 border-slate-900 bg-white px-4 font-heading text-sm font-bold uppercase tracking-[0.08em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
    >
      <FileText size={15} /> Émettre la facture officielle
    </button>
  );
};
